/**
 * Replay: walk the chart forward one bar at a time.
 *
 * **Two things here are integrity properties, not features.**
 *
 * 1. **A replaying chart must be unmistakably not live.** A trader who glances
 *    at a replay and reads it as the current market can act on a price from
 *    three weeks ago. The badge is loud, permanent, and states the bar's actual
 *    timestamp — and the live price in the header is suppressed while it runs.
 *
 * 2. **AI annotations are hidden during replay, and the reason is said out
 *    loud.** The analysis was computed over the complete series, so showing it
 *    against a partially revealed chart would be lookahead bias: the annotation
 *    "knows" what happens next. Replay exists precisely to test a read without
 *    that knowledge, so silently leaving the annotations on would defeat the
 *    only thing the feature is for — and would flatter the analysis while doing
 *    it.
 *
 * Everything else is transport controls.
 */
import { Icon } from './Icon.js';
import { useEffect, useRef } from 'react';
import type { JSX } from 'react';
import type { Bar } from '@aegis/api-client';

/** Wall-clock milliseconds per bar at 1x. Slow enough to actually watch. */
const BASE_INTERVAL_MS = 1200;
export const REPLAY_SPEEDS = [0.5, 1, 2, 4, 10] as const;

export interface ReplayState {
  readonly active: boolean;
  readonly playing: boolean;
  /** How many bars of the series are revealed. */
  readonly cursor: number;
  readonly speed: number;
  /**
   * Steps into the bar currently forming, when replaying intrabar.
   *
   * A step is one bar of the finest timeframe the feed actually holds — real
   * data, not an interpolated path. Zero means the forming bar has not started
   * and only complete bars are shown.
   */
  readonly step: number;
  /** How many steps make one chart bar. 1 means no intrabar detail exists. */
  readonly stepsPerBar: number;
}

export const IDLE_REPLAY: ReplayState = {
  active: false, playing: false, cursor: 0, speed: 1, step: 0, stepsPerBar: 1,
};

/**
 * The bar currently forming, rebuilt from the finer series.
 *
 * Open from the first step, high and low from the steps revealed so far, close
 * from the latest — a real partial bar made of real data.
 *
 * **Not interpolated.** Synthesising a path inside a completed bar would invent
 * the intrabar sequence, which is precisely what replay exists to withhold: a
 * trader practising against an invented path is practising against a market
 * that behaved as the interpolator assumed. Returns null when there is no finer
 * data covering the window, and the caller shows the complete bars alone.
 */
export function formingBar(
  bar: Bar, fineBars: readonly Bar[], step: number, stepsPerBar: number, barSeconds: number,
): Bar | null {
  if (step <= 0 || stepsPerBar <= 1) return null;
  const until = bar.time + (step * barSeconds) / stepsPerBar;
  const inside = fineBars.filter((b) => b.time >= bar.time && b.time < until);
  if (inside.length === 0) return null;
  return {
    time: bar.time,
    open: inside[0]!.open,
    high: Math.max(...inside.map((b) => b.high)),
    low: Math.min(...inside.map((b) => b.low)),
    close: inside[inside.length - 1]!.close,
    volume: inside.reduce((n, b) => n + b.volume, 0),
  };
}

/**
 * Advance one tick of the replay clock.
 *
 * Steps through the forming bar when there is intrabar data, then rolls to the
 * next bar. Exported and pure so the sequencing is testable without a timer —
 * an off-by-one here shows up as a bar that is skipped or shown twice, which is
 * exactly the kind of thing a trader would notice and not be able to describe.
 */
export function advance(state: ReplayState, total: number): ReplayState {
  const next = state.step + 1;
  if (next < state.stepsPerBar) {
    return { ...state, step: next };
  }
  const cursor = Math.min(total, state.cursor + 1);
  return {
    ...state,
    cursor,
    step: 0,
    // Stop at the end rather than looping. A replay that silently wrapped back
    // to the start would put an old bar on screen with no transition.
    playing: cursor < total,
  };
}

export function ReplayControls({
  state, total, barTime, onChange, onExit, locked, onUpgrade,
  maxSteps = 1, stepLabel = '1m', fineNote = '',
}: {
  state: ReplayState;
  total: number;
  /** Epoch seconds of the bar currently at the cursor, for the badge. */
  barTime: number | null;
  onChange(next: ReplayState): void;
  onExit(): void;
  locked: boolean;
  onUpgrade(): void;
  /** How many finer bars the feed holds per chart bar. 1 disables the toggle. */
  maxSteps?: number;
  /** The finest timeframe available, named for the control. */
  stepLabel?: string;
  /** What the API says about what is and is not simulated. */
  fineNote?: string;
}): JSX.Element | null {
  const fine = state.stepsPerBar > 1;
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  // The clock. Restarted whenever speed changes, so a speed change takes
  // effect on the next bar rather than after the current interval.
  useEffect(() => {
    if (!state.active || !state.playing) return undefined;

    // Divided by the step count so a bar takes the same wall-clock time whether
    // it is revealed at once or in five pieces. Otherwise turning on intrabar
    // replay would silently make the whole session five times slower.
    const perStep = BASE_INTERVAL_MS / state.speed / Math.max(1, state.stepsPerBar);

    timer.current = setInterval(() => {
      onChange(advance(state, total));
    }, perStep);

    return () => { if (timer.current) clearInterval(timer.current); };
  }, [state, total, onChange]);

  if (!state.active) {
    return (
      <button
        className="draw-tool compact replay-open"
        onClick={() => (locked ? onUpgrade() : onChange({
          active: true, playing: false,
          // Start two-thirds in: far enough back to have something to walk
          // forward through, recent enough to be a market they recognise.
          cursor: Math.max(1, Math.floor(total * 0.66)),
          speed: 1, step: 0, stepsPerBar: 1,
        }))}
        title={locked ? 'Replay is part of Pro' : 'Replay (bar by bar)'}
        aria-label="Replay"
      >
        <Icon name="replay" />
      </button>
    );
  }

  const atEnd = state.cursor >= total;

  return (
    <div className="replay-bar" role="toolbar" aria-label="Replay controls">
      {/* Loud, permanent, and carrying the actual timestamp. Someone glancing
          at this must not read it as the live market. */}
      <span className="replay-badge">
        REPLAY
        {barTime !== null && (
          <span className="replay-clock mono">
            {new Date(barTime * 1000).toLocaleString(undefined, {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
            })}
            {/* The forming bar is incomplete, and the badge says so rather than
                showing a candle that looks closed. */}
            {fine && state.step > 0 && ` +${state.step}/${state.stepsPerBar}`}
          </span>
        )}
      </span>

      <button
        className="draw-tool compact"
        onClick={() => onChange({ ...state, playing: !state.playing, cursor: atEnd ? 1 : state.cursor })}
        aria-label={state.playing ? 'Pause' : 'Play'}
        title={state.playing ? 'Pause' : atEnd ? 'Replay from the start' : 'Play'}
      >
        <Icon name={state.playing ? 'pause' : 'play'} />
      </button>

      <button
        className="draw-tool compact"
        onClick={() => onChange({ ...advance(state, total), playing: false })}
        disabled={atEnd}
        aria-label={fine ? 'Step forward one interval' : 'Step forward one bar'}
        title={fine ? `Step one ${stepLabel}` : 'Step one bar'}
      >
        <Icon name="step" />
      </button>

      {/* Only offered when the feed actually holds finer bars. A toggle that
          produced an interpolated path would be inventing the intrabar
          sequence, which is the one thing replay exists to withhold. */}
      {maxSteps > 1 && (
        <label className="toggle replay-fine" title={fineNote}>
          <input
            type="checkbox"
            checked={fine}
            onChange={(e) => onChange({
              ...state, step: 0, stepsPerBar: e.target.checked ? maxSteps : 1,
            })}
          />
          {stepLabel} steps
        </label>
      )}

      <input
        className="replay-scrub"
        type="range"
        min={1}
        max={Math.max(1, total)}
        value={state.cursor}
        onChange={(e) => onChange({ ...state, playing: false, cursor: Number(e.target.value) })}
        aria-label="Position in the series"
      />

      <select
        className="input compact-select"
        value={state.speed}
        onChange={(e) => onChange({ ...state, speed: Number(e.target.value) })}
        aria-label="Replay speed"
      >
        {REPLAY_SPEEDS.map((s) => <option key={s} value={s}>{s}×</option>)}
      </select>

      <button className="btn btn-quiet compact" onClick={onExit}>Exit replay</button>
    </div>
  );
}
